<template>
    <template v-if="blockHoisted && sm && !isSidebar">
        <header
            class="tl-navigation tl-navigation-mobile"
            :class="{ expanded }"
        >
            <button
                type="button"
                class="tl-navigation-toggle"
                aria-label="Menu"
                :aria-expanded="expanded"
                @click="toggle"
            >
                <span></span>
                <span></span>
                <span></span>
            </button>

            <div class="tl-navigation-brand">
                {{ mobileHeading }}
            </div>

            <nav
                class="tl-navigation-overlay"
                :class="{ 'events-disabled': disableNavigationEvents }"
            >
                <div
                    v-for="(group, idx) in itemGroups"
                    :key="idx"
                    class="tl-navigation-group"
                    :class="groupClasses"
                >
                    <template v-if="itemGroups.length > 1">
                        <button
                            type="button"
                            class="tl-navigation-group-header"
                            :aria-expanded="expandedGroups[idx]"
                            @click="toggleGroup(idx)"
                        >
                            <img
                                v-if="group.icon"
                                class="tl-navigation-group-icon"
                                :src="group.icon"
                                alt="icon"
                            />
                            <span class="tl-navigation-group-label">{{ group.label }}</span>

                            <i
                                v-if="!disableNavigationEvents && showGroupToggle"
                                :class="expandedGroups[idx] ? 'mi-chevron-up' : 'mi-chevron-down'"
                            />

                            <div
                                v-if="progressBarPosition"
                                class="tl-navigation-group-header-progress-bar"
                                :class="getProgressBarClasses(idx)"
                            />
                        </button>
                    </template>
                    <div
                        v-show="expandedGroups[idx]"
                        class="tl-navigation-group-content"
                    >
                        <SelectionList
                            :horizontal="horizontal"
                            :progress-bar-position="progressBarPosition"
                            :items="group.items"
                            :loading="loading"
                            :custom-css-variables="customCssVariables"
                            @item-chosen="(item) => $emit('itemChosen', item)"
                        />
                    </div>
                </div>

            </nav>
        </header>
    </template>
    <nav
        v-else-if="!blockHoisted && (!sm || isSidebar)"
        :id="id"
        class="tl-navigation"
        :class="{ 'events-disabled': disableNavigationEvents }"
    >
        <div
            v-if="isSidebar"
            class="tl-navigation-logo"
        >
            <img
                v-if="sm && logoMobile"
                :src="logoMobile"
                alt="logo"
            />
            <img
                v-if="!sm && logoDesktop"
                :src="logoDesktop"
                alt="logo"
            />
        </div>

        <div
            v-for="(group, idx) in itemGroups"
            :key="idx"
            class="tl-navigation-group"
            :class="groupClasses"
        >
            <template v-if="itemGroups.length > 1">
                <button
                    type="button"
                    class="tl-navigation-group-header"
                    :aria-expanded="expandedGroups[idx]"
                    @click="toggleGroup(idx)"
                >
                    <img
                        v-if="group.icon"
                        class="tl-navigation-group-icon"
                        :src="group.icon"
                        alt="icon"
                    />
                    <span
                        v-if="showAllElements"
                        class="tl-navigation-group-label"
                    >{{ group.label }}</span>

                    <i
                        v-if="!disableNavigationEvents && showGroupToggle && !(isSidebar && sm)"
                        :class="{
                            'mi-chevron-up': expandedGroups[idx],
                            'mi-chevron-down': !expandedGroups[idx],
                        }"
                    />

                    <div
                        v-if="progressBarPosition && !horizontal"
                        class="tl-navigation-group-header-progress-bar"
                        :class="getProgressBarClasses(idx)"
                    />
                </button>
            </template>
            <div
                v-show="showAllElements && expandedGroups[idx] || itemGroups.length === 1"
                class="tl-navigation-group-content"
            >
                <SelectionList
                    :horizontal="horizontal"
                    :progress-bar-position="progressBarPosition"
                    :items="group.items"
                    :loading="loading"
                    :custom-css-variables="customCssVariables"
                    @item-chosen="(item) => $emit('itemChosen', item)"
                />
            </div>
        </div>
    </nav>
</template>

<script setup lang="ts">
import type { NavigationItemGroup, ProgressBarPosition, SelectionListItem } from 'widget-components/integrations/CustomPage/types'
import type { CustomCssVariables } from 'widget-components/types'
import SelectionList from 'widget-components/components/SelectionList/SelectionList.vue'
import { computed, ref } from 'vue'
import { useBreakpoints } from 'widget-components/composables/breakpoints'
import { useEventBus } from '@vueuse/core'

defineOptions({ inheritAttrs: false })

const { sm } = useBreakpoints()

const props = defineProps<{
    id?: string
    horizontal?: boolean
    itemGroups: NavigationItemGroup[]
    loading?: boolean
    blockHoisted?: boolean
    mobileHeading?: string
    customCssVariables?: CustomCssVariables
    mobileNavigationType?: string
    showGroupToggle?: boolean
    disableNavigationEvents?: boolean
    progressBarPosition?: ProgressBarPosition
    logoDesktop?: string
    logoMobile?: string
}>()

const isSidebar = computed(() => props.mobileNavigationType === 'sidebar')

const showAllElements = computed(() => !isSidebar.value || !sm.value)

defineEmits<{
    (e: 'itemChosen', item: SelectionListItem): void
}>()

const expanded = ref(false)

const getInitiallyExpandedGroups = () => {
    const expandedIds: Record<number, boolean> = {}

    props.itemGroups.forEach((grp, grpIdx) => {
        if (grp.items.some(i => i.navigationOption?.active)) {
            expandedIds[grpIdx] = true
        }
    })

    return !Object.keys(expandedIds).length
        ? { 0: true }
        : expandedIds
}

const expandedGroups = ref<Record<number, boolean>>(getInitiallyExpandedGroups())

const toggleGroup = (idx: number) => {
    expandedGroups.value[idx] = !expandedGroups.value[idx]
}

const groupClasses = computed(() => ({
    'no-progress': !props.progressBarPosition,
    'with-header': props.itemGroups.length > 1,
    before: props.progressBarPosition === 'before',
    after: props.progressBarPosition === 'after',
}))

const getProgressBarClasses = (idx: number) => {
    const classes: string[] = []

    if (!props.itemGroups[idx]) {
        return classes
    }

    if (props.itemGroups[idx].items.every(i => !i.navigationOption)) {
        classes.push('no-progress')
    }

    if (props.progressBarPosition) {
        classes.push(props.progressBarPosition)
    }

    if (props.itemGroups[idx].items.some(i => i.navigationOption && (i.navigationOption.selected || i.navigationOption.active))) {
        classes.push('selected')
    }

    if ((isSidebar.value && sm.value) && props.itemGroups[idx].items.some(i => i.navigationOption && i.navigationOption.active)) {
        classes.push('active')
    }

    return classes
}

const handleEscKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
        event.stopPropagation()
        toggle()
    }
}

const toggle = () => {
    expanded.value = !expanded.value

    if (expanded.value) {
        document.addEventListener('keydown', handleEscKey, { capture: true })
    } else {
        document.removeEventListener('keydown', handleEscKey, { capture: true })
    }
}

// the overlay would otherwise cover the page and the error it now shows
useEventBus('custom-page-action-failed').on(() => {
    if (expanded.value) {
        toggle()
    }
})

</script>

<style lang="sass" scoped>

$z-index-base: 50
$navbar-height: 56px

.events-disabled *
    pointer-events: none

.tl-navigation
    :deep(.tl-selection-list-tile .text-title)
        font-size: var(--tl-navigation-item-font-size, var(--tl-font-size, 16px))
        font-weight: var(--tl-navigation-item-font-weight, var(--tl-font-weight, var(--font-weight-semibold)))

    :deep(.tl-selection-list-tile .text-paragraph)
        font-size: var(--tl-navigation-item-font-size, var(--tl-font-size, 16px))
        font-weight: var(--tl-navigation-item-font-weight, var(--tl-font-weight, var(--font-weight-regular)))

    :deep(.tl-selection-list-tile.tl-selection-list-tile-active .text-title)
        font-weight: var(--tl-container-active-item-font-weight, 900)

    &-mobile
        position: sticky
        top: 0
        z-index: $z-index-base
        display: flex
        gap: 8px
        align-items: center
        height: $navbar-height
        background: var(--tl-navigation-mobile-bar-bgcolor, var(--tl-widget-background-color))
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)

        &.expanded
            z-index: calc(var(--layer-widget-close-button, $z-index-base) + 1)

    &-brand
        font-size: 1.2rem
        font-weight: bold
        overflow: hidden
        text-overflow: ellipsis

    &-toggle
        position: relative
        width: 40px
        height: 40px
        padding: 8px
        border: none
        background: none
        border-radius: 50%
        cursor: pointer
        overflow: visible
        z-index: $z-index-base + 2
        flex-shrink: 0

        span
            position: absolute
            left: 50%
            width: 24px
            height: 2px
            background: var(--tl-container-text-primary-color, var(--color-black))
            border-radius: 2px
            transform-origin: center
            transition: transform 0.28s cubic-bezier(.2,.8,.2,1), opacity 0.2s
            transform: translateX(-50%) translateY(0)

            &:nth-child(1)
                top: calc(50% - 8px)
            &:nth-child(2)
                top: 50%
            &:nth-child(3)
                top: calc(50% + 8px)

        .expanded > &
            background: var(--tl-navigation-mobile-overlay-bgcolor, var(--tl-widget-background-color))

            span:nth-child(1)
                transform: translateX(-50%) translateY(8px) rotate(45deg)
            span:nth-child(2)
                opacity: 0
                transform: translateX(-50%) scaleX(0)
            span:nth-child(3)
                transform: translateX(-50%) translateY(-8px) rotate(-45deg)

    &-overlay
        position: absolute
        inset: 0
        height: 100vh
        z-index: $z-index-base - 1
        overflow: auto
        background: var(--tl-navigation-mobile-overlay-bgcolor, var(--tl-widget-background-color))
        padding: 56px 16px
        gap: 1.5rem
        font-size: 1.5rem
        opacity: 0
        pointer-events: none
        transition: opacity var(--tl-transition-duration) ease

        .expanded > &
            opacity: 1
            pointer-events: all

        &-group-label
            font-weight: bold
            font-size: 1rem
            padding: 20px 10px
            text-overflow: ellipsis
            overflow: hidden
            color: var(--tl-container-text-secondary-color, #6b7280)

    &-logo
        display: flex
        margin: var(--tl-navigation-logo-margin, 15px)
        img
            width: 100%

    &-group
        position: relative
        margin-bottom: var(--tl-container-item-gap)
        margin-left: 0

        &.no-progress
            --tl-navigation-progress-bar-margin: 0
            --tl-navigation-progress-bar-item-gap: 0

        &.before
            margin-left: calc(var(--tl-navigation-progress-bar-width, 4px) + var(--tl-navigation-progress-bar-item-gap, 10px) + var(--tl-navigation-progress-bar-margin, 10px))

        &.after
            margin-right: calc(var(--tl-navigation-progress-bar-width, 4px) + var(--tl-navigation-progress-bar-item-gap, 10px) + var(--tl-navigation-progress-bar-margin, 10px) - var(--tl-container-border-size, 1px))

        &-header
            display: flex
            align-items: center
            justify-content: space-between
            width: 100%
            height: var(--tl-navigation-group-header-height, 60px)
            padding: 0 20px
            border: none
            background: none
            cursor: pointer
            font-weight: bold
            font-size: var(--tl-navigation-item-font-size, var(--tl-font-size, 0.95rem))
            color: var(--tl-container-text-primary-color, inherit)
            margin-bottom: var(--tl-container-item-gap)
            word-break: break-word

            &:hover
                opacity: 0.8

            &-progress-bar
                position: absolute
                height: var(--tl-navigation-group-header-height, 60px)
                top: 0
                right: calc((-1 * (var(--tl-navigation-progress-bar-width, 4px) + var(--tl-navigation-progress-bar-item-gap, 10px))))
                width: var(--tl-navigation-progress-bar-width, 4px)
                border-radius: var(--tl-navigation-progress-bar-border-radius, 4px)
                background: var(--tl-navigation-progress-bar-default-color, var(--tl-container-border-color))

                &.selected
                    background: var(--tl-navigation-progress-bar-selected-color, var(--color-black))

                &.active
                    &::after
                        content: ''
                        position: absolute
                        top: 50%
                        left: 50%
                        transform: translate(-50%, -50%)
                        width: var(--tl-navigation-progress-bar-active-mark-size, 10px)
                        height: var(--tl-navigation-progress-bar-active-mark-size, 10px)
                        border-radius: 50%
                        background: var(--tl-navigation-progress-bar-active-mark-color, var(--tl-navigation-progress-bar-selected-color, var(--color-black)))

                &.before
                    left: calc((-1 * (var(--tl-navigation-progress-bar-width, 4px) + var(--tl-navigation-progress-bar-item-gap, 10px))))


                &.after
                    right: calc((-1 * (var(--tl-navigation-progress-bar-width, 4px) + var(--tl-navigation-progress-bar-item-gap, 10px))))

        &-label
            width: 100%
            text-align: left

        &-icon
            max-width: 100%
            max-height: 100%

            &:not(.sm)
                margin-right: 1em

        &-chevron
            transition: transform 0.2s ease
            flex-shrink: 0

            &.expanded
                transform: rotate(180deg)

        &.with-header
            .tl-navigation-group-content
                padding-left: var(--tl-navigation-group-indent, 20px)
</style>
